import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  useExportedFormFiles,
  exportedFormFilesKeys,
} from 'components/FileManagement/hooks/useExportedFormFiles';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import {
  NotebookFile,
  filesOrFoldersKeys,
} from 'components/FileManagement/hooks/useGetAllFiles';
import {
  postExportAsForm,
  postPersistIcsFormExport,
} from 'hooks/incident/useExportForm';
import {
  selectGroupGuid,
  selectIncident,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';
import downloadFile from 'utils/downloadFile';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

type UseIcs201Op0Return = {
  existing201File: NotebookFile | undefined;
  hasExported: boolean;
  exportIcs201Op0: (shouldDownload?: boolean) => Promise<void>;
  displayDocuments: { uri: string; fileName: string }[] | undefined;
  isLoading: boolean;
};

export const useIcs201Op0 = (incidentId?: string): UseIcs201Op0Return => {
  const queryClient = useQueryClient();
  const incident = useAppSelector(selectIncident);
  const user = useAppSelector(selectUser);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { captureMapSketchFile } = useUscgMapContext();
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const { data: exportedForms = [], isLoading: isLoadingForms } =
    useExportedFormFiles(incidentId);

  const [displayDocuments, setDisplayDocuments] =
    useState<{ uri: string; fileName: string }[]>();
  const [isExporting, setIsExporting] = useState(false);

  const findIcs201Op0File = useMemo(() => {
    // Heuristic: match ICS 201 and OP0 regardless of separators/case
    const regex201 = /ICS[-_ ]?201/i;
    const regexOp0 = /OP[-_ ]?0/i;
    return (files: NotebookFile[]) =>
      files
        .filter((f) => regex201.test(f.file_name) && regexOp0.test(f.file_name))
        .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
        .at(0);
  }, []);

  const existing201File = useMemo(
    () => findIcs201Op0File(exportedForms as NotebookFile[]),
    [exportedForms, findIcs201Op0File]
  );

  useEffect(() => {
    if (!existing201File) return;

    (async () => {
      try {
        const url = await getSignedUrl(existing201File.fileUrl);
        setDisplayDocuments([
          { uri: url, fileName: existing201File.file_name },
        ]);
      } catch (error) {
        console.error('Failed to fetch signed URL:', error);
      }
    })();
  }, [existing201File, getSignedUrl]);

  const exportIcs201Op0 = async (shouldDownload: boolean = true) => {
    // Export ICS-201 for OP0; include map sketch if available
    setIsExporting(true);
    try {
      const mapSketch = await captureMapSketchFile();
      const result = await postExportAsForm(
        {
          incident,
          user,
          groupGuid,
        },
        {
          formname: 'ICS_201-CG',
          data: {},
          mapSketch,
          periodNumber: 0,
        }
      );

      // Download the file
      if (shouldDownload) downloadFile(result.formname, 'PDF', result.data);

      // Persist to workspace
      await postPersistIcsFormExport(
        {
          incident,
          user,
          groupGuid,
        },
        {
          pdfBase64: result.data,
          workspaceFormname: result.formname,
          periodNumber: 0,
        }
      );

      await queryClient.refetchQueries({
        queryKey: filesOrFoldersKeys.allFiles(incident.id),
      });

      await queryClient.refetchQueries({
        queryKey: exportedFormFilesKeys.byIncident(incident.id),
      });
    } catch (error) {
      console.error(error);
      toast.error('Error generating ICS-201.');
    } finally {
      setIsExporting(false);
    }
  };

  return {
    existing201File,
    hasExported: !!existing201File,
    exportIcs201Op0,
    displayDocuments,
    isLoading: isLoadingForms || isExporting,
  };
};
